import { mutateStoreyInDesign } from './design-storey.mutator';
import { mutateActivity } from './design-activity.mutator';
import { getStoreyById, StoreySelector } from '../storey.helpers';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { getActivityByType } from '../activities.helpers';
import { throwIfError } from '@shared/helpers/utils.helpers';
import { required } from '@shared/helpers/function.helpers';
import { activityRefGuards } from '../guards/activity-guards.helpers';

/**
 * Helper to mutate a design by adding/removing/updating activities and storeys
 */
export const mutateDesign = {
  activity: mutateActivity,
  storey: mutateStoreyInDesign,
} as const;

/**
 * Add an activity reference to a storey by type.
 * Will add the activity if it doesn't exist.
 * Mostly intended for unit test usage
 * @param design Building design to add the activity reference to
 * @param type Activity type to add the activity reference to
 * @param storeyOrIds Storeys to add the activity reference to
 * @returns Building design with the activity reference added
 */
export const addActivityReferenceByType = (
  design: BuildingDesign,
  type: ActivityType,
  ...storeySelectors: StoreySelector[]
): BuildingDesign => {
  if (storeySelectors.length === 0) {
    return design;
  }

  // Add activity if it doesn't exist
  if (!getActivityByType(design, type)) {
    design = throwIfError(mutateActivity.add(design, { type })); // TODO: Handle better than throwing an error;
  }
  const activity = required(
    getActivityByType(design, type),
    'Activity not found, should have been added above',
  );

  for (const storeySelector of storeySelectors) {
    const storey = getStoreyById(design, storeySelector);
    if (!storey) return design;

    // Only add the activityRef if it doesn't already exist
    if (storey.activities.some((ref) => ref.type === type)) continue;

    // Set a balanced share for the new activity reference (second will get 50%, third 33%, etc.)
    const share =
      1 /
      (storey.activities.filter(activityRefGuards.is.programActivityRef)
        .length +
        1);

    design = mutateStoreyInDesign.activityRef.add(
      design,
      storeySelector,
      activity.id,
      type,
      share,
    );
  }
  return design;
};
